import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Card,
    CardContent,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Typography
} from "@mui/material";
import {FastField, Field, getIn, useFormikContext} from "formik";
import {CheckboxWithLabel, Select, TextField} from "formik-mui";
import {FullWidthText} from "../formFields";
import React from "react";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {sprintf} from "sprintf-js";


const OptionalsDefaults = () => {
    return {
        "c": {
            "1": {
                "quality-floors-wall-cover": false,
                "fireplace-stove": false,
                "balcony-patio-porch": false,
                "special-windows-doors": false,
                "exceptional-size": false,
                "other": false,
                "other-specify": ""
            },
            "2": {
                "dishwasher": false,
                "separate-freezer": false,
                "garbage-disposal": false,
                "counter-nook": false,
                "pantry-shelving-cabinets": false,
                "double-self-cleaning-microwave": false,
                "double-sink": false,
                "hq-cabinets": false,
                "abundant-countertop": false,
                "modern-appliance": false,
                "exceptional-size": false,
                "other": false,
                "other-specify": ""
            },
            "3": {
                "quality-floors-wall-cover": false,
                "fireplace-stove": false,
                "balcony-patio-porch": false,
                "special-windows-doors": false,
                "exceptional-size": false,
                "other": false,
                "other-specify": ""
            },
            "4": {
                "special-shower-head": false,
                "heat-lamp": false,
                "large-mirrors": false,
                "glass-door": false,
                "separate-dressing-room": false,
                "double-sink-special-lavatory": false,
                "exceptional-size": false,
                "other": false,
                "other-specify": ""
            },
            "5": {
                "storm-windows-doors": false,
                "weatherization": false,
                "screen-doors-windows": false,
                "good-upkeep": false,
                "garage-parking-driveway": false,
                "large-yard": false,
                "good-maintenance": false,
                "other": false,
                "other-specify": false,
            },
            "6": {
                "accessibility": false,
                "accessibility-specify": ""
            }
        },
        "d": {
            "1": "",
            "2": "",
            "3": "",
            "4": "",
            "5": {
                "refrigerator": "",
                "microwave": "",
                "range": ""
            },
            "6": "",
        }
    };
};

/*
* Validate the "Please Specify" input field if and only if the corresponding
* "Other" checkbox has been checked. Because the "Please Specify" input
* field is created only after the "Other" checkbox has been checked, we do
* not need to know if the "Other" checkbox has been selected.
* */
const validateOtherSpecify = (value) => {
    if (value.length === 0) {
        return "Must not be blank";
    } else {
        if (value.length > 36) {
            return "May not be more than 36 characters"
        }
    }
    return "";
};

const validateCommentBox = (value) => {
    if (value.length > 512) {
        return "May not be more than 512 characters (roughly 5 lines)."
    }
    return "";
};

const validateTenantCount = (value) => {
    if ((value > 99) || (value < 1)) {
        return "Must be more than zero and less than 100";
    }
};

//TODO (mmilburn): Make sure FastField is appropriate!
const GridCheckbox = (props) => {
    const {name, title, xs} = props;
    return (<>
            <Grid item xs={xs}>
                <FastField
                    component={CheckboxWithLabel}
                    type={"checkbox"}
                    name={name}
                    Label={{label: title}}
                />
            </Grid>
        </>
    );
}

/*
* Create an accompanying FullWidthText for any checkbox with props.name only
* when that checkbox has been checked. Further, ensure that this new field is
* filled out.
* */
const SpecifyCheckbox = (props) => {
    const {values, setFieldValue} = useFormikContext();
    const {name, title, xs} = props;
    const dependency = getIn(values, name);
    const otherTextName = sprintf("%s-specify", name);
    const checkboxOnly = (<GridCheckbox
        name={name}
        title={title}
        xs={xs}
    />);
    const checkboxAndText = (
        <>
            <Grid item xs={xs}>
                <Field
                    component={CheckboxWithLabel}
                    type={"checkbox"}
                    name={name}
                    Label={{label: title}}
                />
            </Grid>
            <Grid item xs={xs}>
                <FullWidthText
                    name={otherTextName}
                    placeholder={"Please specify"}
                    validate={validateOtherSpecify}
                />
            </Grid>
        </>
    );

    React.useEffect(() => {
        if (!dependency) {
            setFieldValue(name, false);
            //Clear text when checkbox is not checked.
            setFieldValue(otherTextName, "");
        }
    }, [dependency, name, otherTextName, setFieldValue]);

    return !!dependency ? checkboxAndText : checkboxOnly;
};

const LivingRoom = (props) => {
    return (
        <>
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                    <Typography variant={"subtitle1"} gutterBottom>
                        1. Living Room
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={1}>
                        <GridCheckbox
                            name={"c.1.quality-floors-wall-cover"}
                            title={"High-quality floors or wall coverings"}
                            xs={12}
                        />
                        <GridCheckbox
                            name="c.1.fireplace-stove"
                            title={"Working fireplace or stove"}
                            xs={12}
                        />
                        <GridCheckbox
                            name={"c.1.balcony-patio-porch"}
                            title={"Balcony, patio, or porch"}
                            xs={12}
                        />
                        <GridCheckbox
                            name={"c.1.special-windows-doors"}
                            title={"Special windows or doors"}
                            xs={12}
                        />
                        <GridCheckbox
                            name={"c.1.exceptional-size"}
                            title={"Exceptional size relative to needs of family"}
                            xs={12}
                        />
                        <SpecifyCheckbox
                            name={"c.1.other"}
                            title={"Other"}
                            xs={12}
                        />
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </>
    );
};

const Kitchen = (props) => {
    return (
        <>
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                    <Typography variant={"subtitle1"} gutterBottom>
                        2. Kitchen
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={1}>
                        <GridCheckbox
                            name={"c.2.dishwasher"}
                            title={"Dishwasher"}
                            xs={12}
                        />
                        <GridCheckbox
                            name="c.2.separate-freezer"
                            title={"Separate freezer"}
                            xs={12}
                        />
                        <GridCheckbox
                            name={"c.2.garbage-disposal"}
                            title={"Garbage disposal"}
                            xs={12}
                        />
                        <GridCheckbox
                            name={"c.2.counter-nook"}
                            title={"Eating counter / breakfast nook"}
                            xs={12}
                        />
                        <GridCheckbox
                            name={"c.2.pantry-shelving-cabinets"}
                            title={"Pantry, abundant shelving, or cabinets"}
                            xs={12}
                        />
                        <GridCheckbox
                            name={"c.2.double-self-cleaning-microwave"}
                            title={"Double oven, self-cleaning oven, or microwave"}
                            xs={12}
                        />
                        <GridCheckbox
                            name={"c.2.double-sink"}
                            title={"Double sink"}
                            xs={12}
                        />
                        <GridCheckbox
                            name={"c.2.hq-cabinets"}
                            title={"High-quality cabinets"}
                            xs={12}
                        />
                        <GridCheckbox
                            name={"c.2.abundant-countertop"}
                            title={"Abundant counter-top space"}
                            xs={12}
                        />
                        <GridCheckbox
                            name={"c.2.modern-appliance"}
                            title={"Modern appliance(s)"}
                            xs={12}
                        />
                        <GridCheckbox
                            name={"c.2.exceptional-size"}
                            title={"Exceptional size relative to needs of family"}
                            xs={12}
                        />
                        <SpecifyCheckbox
                            name={"c.2.other"}
                            title={"Other"}
                            xs={12}
                        />
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </>
    );
};

const OtherRooms = (props) => {
    return (
        <>
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                    <Typography variant={"subtitle1"} gutterBottom>
                        3. Other Rooms Used for Living
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={1}>
                        <GridCheckbox
                            name={"c.3.quality-floors-wall-cover"}
                            title={"High-quality floors or wall coverings"}
                            xs={12}
                        />
                        <GridCheckbox
                            name="c.3.fireplace-stove"
                            title={"Working fireplace or stove"}
                            xs={12}
                        />
                        <GridCheckbox
                            name={"c.3.balcony-patio-porch"}
                            title={"Balcony, patio, or porch"}
                            xs={12}
                        />
                        <GridCheckbox
                            name={"c.3.special-windows-doors"}
                            title={"Special windows or doors"}
                            xs={12}
                        />
                        <GridCheckbox
                            name={"c.3.exceptional-size"}
                            title={"Exceptional size relative to needs of family"}
                            xs={12}
                        />
                        <SpecifyCheckbox
                            name={"c.3.other"}
                            title={"Other"}
                            xs={12}
                        />
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </>
    );
};

const Bath = (props) => {
    return (
        <>
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                    <Typography variant={"subtitle1"} gutterBottom>
                        4. Bath
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={1}>
                        <GridCheckbox
                            name={"c.4.special-shower-head"}
                            title={"Special feature in shower head"}
                            xs={12}
                        />
                        <GridCheckbox
                            name="c.4.heat-lamp"
                            title={"Built-in heat lamp"}
                            xs={12}
                        />
                        <GridCheckbox
                            name={"c.4.large-mirrors"}
                            title={"Large mirrors"}
                            xs={12}
                        />
                        <GridCheckbox
                            name={"c.4.glass-door"}
                            title={"Glass door on shower / tub"}
                            xs={12}
                        />
                        <GridCheckbox
                            name={"c.4.separate-dressing-room"}
                            title={"Separate dressing room"}
                            xs={12}
                        />
                        <GridCheckbox
                            name={"c.4.double-sink-special-lavatory"}
                            title={"Double sink or special lavatory"}
                        />
                        <GridCheckbox
                            name={"c.4.exceptional-size"}
                            title={"Exceptional size relative to needs of family"}
                            xs={12}
                        />
                        <SpecifyCheckbox
                            name={"c.4.other"}
                            title={"Other"}
                            xs={12}
                        />
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </>
    );
};

const Overall = (props) => {
    return (<>
        <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                <Typography variant={"subtitle1"} gutterBottom>
                    5. Overall Characteristics
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container spacing={1}>
                    <GridCheckbox
                        name={"c.5.storm-windows-doors"}
                        title={"Storm windows & doors"}
                        xs={12}
                    />
                    <GridCheckbox
                        name="c.5.weatherization"
                        title={"Other forms of weatherization (e.g. insulation, weather stripping)"}
                        xs={12}
                    />
                    <GridCheckbox
                        name={"c.5.screen-doors-windows"}
                        title={"Screen doors or windows"}
                        xs={12}
                    />
                    <GridCheckbox
                        name={"c.5.good-upkeep"}
                        title={"Good upkeep of grounds (i.e. site cleanliness, landscaping)"}
                        xs={12}
                    />
                    <GridCheckbox
                        name={"c.5.garage-parking-driveway"}
                        title={"Garage, parking facilities, or driveway"}
                        xs={12}
                    />
                    <GridCheckbox
                        name={"c.5.large-yard"}
                        title={"Large yard"}
                    />
                    <GridCheckbox
                        name={"c.5.good-maintenance"}
                        title={"Good maintenance of building exterior"}
                        xs={12}
                    />
                    <SpecifyCheckbox
                        name={"c.5.other"}
                        title={"Other"}
                        xs={12}
                    />
                </Grid>
            </AccordionDetails>
        </Accordion>
    </>);
};

const Accessibility = (props) => {
    return (<>
        <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                <Typography variant={"subtitle1"} gutterBottom>
                    6. Disabled Accessibility
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container spacing={1}>
                    <SpecifyCheckbox
                        name={"c.6.accessibility"}
                        title={"Unit is accessible to a particular disability (specify disability)"}
                        xs={12}
                    />
                </Grid>
            </AccordionDetails>
        </Accordion>
    </>)
};

const Amenities = (props) => {
    return (
        <>
            <Card>
                <CardContent>
                    <Typography variant={"h6"} gutterBottom>
                        C. Special Amenities
                    </Typography>
                    <LivingRoom/>
                    <Kitchen/>
                    <OtherRooms/>
                    <Bath/>
                    <Overall/>
                    <Accessibility/>
                </CardContent>
            </Card>
        </>
    );
};

const TenantQuestions = (props) => {
    return (<>
        <Card>
            <CardContent>
                <Typography variant={"h6"} gutterBottom>
                    D. Questions to ask the Tenant
                </Typography>
                <Grid item>
                    <Typography variant={"subtitle1"} gutterBottom>
                        1. Does the owner make repairs when asked?
                    </Typography>
                    <FormControl fullWidth>
                        <InputLabel htmlFor={"d.1"}>Yes / No</InputLabel>
                        <FastField
                            component={Select}
                            name={"d.1"}
                            inputProps={{id: "d.1"}}
                        >
                            <MenuItem value={"yes"}>Yes</MenuItem>
                            <MenuItem value={"no"}>No</MenuItem>
                            <MenuItem value={""}>No Answer</MenuItem>
                        </FastField>
                    </FormControl>
                </Grid>
                <Grid item>
                    <Typography variant={"subtitle1"} gutterBottom>
                        2. How many people live there?
                    </Typography>
                    <FullWidthText
                        name={"d.2"}
                        label={"Number of People"}
                        validate={validateTenantCount}
                    />
                </Grid>
                <Grid item>
                    <Typography variant={"subtitle1"} gutterBottom>
                        3. How much money do you pay to the owner / agent for rent?
                    </Typography>
                    <FullWidthText
                        name={"d.3"}
                        label={"Round up to nearest dollar"}
                        placeholder={"$"}
                    />
                </Grid>
                <Grid item>
                    <Typography variant={"subtitle1"} gutterBottom>
                        4. Do you pay for anything else?
                    </Typography>
                    <FullWidthText
                        name={"d.4"}
                        label={"Comma separated list (e.g. water, internet)"}
                    />
                </Grid>
                <Grid item>
                    <Typography variant={"subtitle1"} gutterBottom>
                        5. Who owns the following?
                    </Typography>
                    <Typography variant={"body2"}>
                        Refrigerator
                    </Typography>
                    <FormControl fullWidth>
                        <InputLabel htmlFor={"d.5.refrigerator"}>Owner / Tenant</InputLabel>
                        <Field
                            component={Select}
                            name={"d.5.refrigerator"}
                            inputProps={{id: "d.5.refrigerator"}}
                        >
                            <MenuItem value={"owner"}>Owner</MenuItem>
                            <MenuItem value={"tenant"}>Tenant</MenuItem>
                            <MenuItem value={""}>No Answer</MenuItem>
                        </Field>
                    </FormControl>
                    <Typography variant={"body2"}>
                        Microwave
                    </Typography>
                    <FormControl fullWidth>
                        <InputLabel htmlFor={"d.5.microwave"}>Owner / Tenant</InputLabel>
                        <Field
                            component={Select}
                            name={"d.5.microwave"}
                            inputProps={{id: "d.5.microwave"}}
                        >
                            <MenuItem value={"owner"}>Owner</MenuItem>
                            <MenuItem value={"tenant"}>Tenant</MenuItem>
                            <MenuItem value={""}>No Answer</MenuItem>
                        </Field>
                    </FormControl>
                    <Typography variant={"body2"}>
                        Range
                    </Typography>
                    <FormControl fullWidth>
                        <InputLabel htmlFor={"d.5.range"}>Owner / Tenant</InputLabel>
                        <Field
                            component={Select}
                            name={"d.5.range"}
                            inputProps={{id: "d.5.range"}}
                        >
                            <MenuItem value={"owner"}>Owner</MenuItem>
                            <MenuItem value={"tenant"}>Tenant</MenuItem>
                            <MenuItem value={""}>No Answer</MenuItem>
                        </Field>
                    </FormControl>
                </Grid>
                <Grid item>
                    <Typography variant={"subtitle1"} gutterBottom>
                        6. Do you have any additional comments or concerns?
                    </Typography>
                    <Field
                        component={TextField}
                        name={"d.6"}
                        multiline
                        fullWidth
                        rows={4}
                        spellCheck={true}
                        variant={"outlined"}
                        validate={validateCommentBox}
                    />
                </Grid>
            </CardContent>
        </Card>
    </>);
};

const Optionals = (props) => {
    return (
        <>
            <Amenities/>
            <TenantQuestions/>
        </>
    );
};

export default Optionals;
export {OptionalsDefaults};